import React, { useContext, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import PropTypes from "prop-types";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import Paper from "@mui/material/Paper";
import { useFetchGetV2 } from "hooks/fetch/useFetchGetV2";
import { Breadcrumbs } from "components/Breadcrumbs";
import ProgressIndicatorContainer from "components/ProgressIndicatorContainer";
import { AppTitleContext } from "app_context";
import PerfectScrollbar from "react-perfect-scrollbar";
import { styled } from "@mui/material/styles";
import { useLoadCounter } from "hooks/useLoadCounter";

export function EntitlementPage() {
  const { id: entitlementId } = useParams();

  const {
    loading: entitlementsLoading,
    error: entitlementsError,
    response: entitlements
  } = useFetchGetV2(`settings/entitlements/${entitlementId}`);

  const {
    loading: userLoading,
    error: userError,
    response: users
  } = useFetchGetV2("users?system=1");

  const tableLoading = entitlementsLoading || userLoading;
  const tableError = entitlementsError || userError;
  const tableLoadCount = useLoadCounter(tableLoading);

  const appTitleContext = useContext(AppTitleContext);

  useEffect(() => {
    appTitleContext.setPageTitle(
      <Breadcrumbs>
        <Link to="/pages/settings">Settings</Link>
        {entitlements && <span>Entitlement: {entitlements?.name}</span>}
      </Breadcrumbs>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entitlements]);

  const usersList = users?.results ?? [];
  const entitlementsUsers = entitlements?.users ?? [];

  const mappedUsers = entitlementsUsers
    .map(id => usersList.find(user => user.id === id))
    .sort((a, b) =>
      `${a.first_name} ${a.last_name}`.localeCompare(
        `${b.first_name} ${b.last_name}`
      )
    );

  return (
    <EntitlementsPaper>
      <EntitlementsHeader>Entitlements</EntitlementsHeader>
      <Typography variant="body2">
        <DescriptionWrapper>
          {`Users with the ${entitlements?.name} entitlement. To add users, add them to a role which has the ${entitlements?.name} entitlement`}
        </DescriptionWrapper>
      </Typography>
      <EntitlementsTableContainer component={Paper}>
        <ProgressIndicatorContainer loading={tableLoading} error={tableError}>
          <PerfectScrollbar>
            <EntitlementsTable stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell data-col={1}>ID</TableCell>
                  <TableCell data-col={2}>Name</TableCell>
                  <TableCell data-col={3}>E-Mail</TableCell>
                </TableRow>
              </TableHead>
              <TableBody
                data-qa_loading={tableLoading}
                data-qa_load_count={tableLoadCount}
              >
                {mappedUsers.map(user =>
                  user ? (
                    <TableRow key={user.id}>
                      <TableCell data-col={1}>{user.id}</TableCell>
                      <TableCell data-col={2}>
                        <Link
                          to={`/pages/settings/user/${user.id}`}
                        >{`${user.first_name} ${user.last_name}`}</Link>
                      </TableCell>
                      <TableCell data-col={3}>{user.email}</TableCell>
                    </TableRow>
                  ) : null
                )}
              </TableBody>
            </EntitlementsTable>
          </PerfectScrollbar>
        </ProgressIndicatorContainer>
      </EntitlementsTableContainer>
    </EntitlementsPaper>
  );
}

const EntitlementsHeader = styled("h3")`
  padding: ${({ theme }) => `${theme.spacing(1)} 0px 0px ${theme.spacing(1)}`};
  margin: 0px;
`;
const EntitlementsPaper = styled(Paper)`
  padding: ${({ theme }) => theme.spacing(1)};
  margin-top: ${({ theme }) => theme.spacing(2)};
`;
const EntitlementsTableContainer = styled(TableContainer)`
  height: 750px;
`;
const EntitlementsTable = styled(Table)`
  height: max-content;
`;
const DescriptionWrapper = styled("div")`
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

EntitlementPage.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string
    })
  })
};
